import { useEffect, useMemo, useState } from "react";
import {
  OnboardingAnswers,
  OnboardingQuestion,
  OnboardingAnswerValue,
  SliderQuestion,
} from "../types";
import { ensureSliderValue, isQuestionAnswered } from "../utils/answerHelpers";

interface UseAssessmentFlowOptions {
  questions: OnboardingQuestion[];
  initialAnswers?: OnboardingAnswers;
}

interface UseAssessmentFlowResult {
  answers: OnboardingAnswers;
  currentIndex: number;
  currentQuestion: OnboardingQuestion | undefined;
  currentAnswer: OnboardingAnswerValue | undefined;
  progress: number;
  isCurrentAnswerValid: boolean;
  setAnswer: (questionId: string, value: OnboardingAnswerValue) => void;
  goToNext: () => void;
  goToPrevious: () => void;
}

export const useAssessmentFlow = ({
  questions,
  initialAnswers,
}: UseAssessmentFlowOptions): UseAssessmentFlowResult => {
  const [answers, setAnswers] = useState<OnboardingAnswers>(
    initialAnswers ?? {}
  );
  const [currentIndex, setCurrentIndex] = useState(0);

  const currentQuestion = questions[currentIndex];
  const currentAnswer = currentQuestion
    ? answers[currentQuestion.id]
    : undefined;

  useEffect(() => {
    if (!currentQuestion) {
      return;
    }

    if (currentQuestion.type === "slider") {
      const sliderQuestion = currentQuestion as SliderQuestion;
      setAnswers((prev) => {
        const existing = prev[sliderQuestion.id];
        if (
          typeof existing === "number" ||
          (typeof existing === "string" && existing === "prefer_not_to_say")
        ) {
          return prev;
        }

        return {
          ...prev,
          [sliderQuestion.id]: ensureSliderValue(sliderQuestion, existing),
        };
      });
      return;
    }
  }, [currentQuestion]);

  const setAnswer = (questionId: string, value: OnboardingAnswerValue) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev >= questions.length - 1 ? prev : prev + 1));
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev <= 0 ? prev : prev - 1));
  };

  const isCurrentAnswerValid = useMemo(() => {
    if (!currentQuestion) {
      return false;
    }
    return isQuestionAnswered(currentQuestion, currentAnswer);
  }, [currentAnswer, currentQuestion]);

  const progress = ((currentIndex + 1) / questions.length) * 100;

  return {
    answers,
    currentIndex,
    currentQuestion,
    currentAnswer,
    progress,
    isCurrentAnswerValid,
    setAnswer,
    goToNext,
    goToPrevious,
  };
};
